import { getErrorMessage } from '@/lib/api/error-utils'
import { createLogger } from '@/lib/logger'
import {
  isRecordQueryInScope,
  scopedRecordKey,
} from '@/lib/realtime/record-query-scope'
import { inventoryApi } from '@/modules/inventory/api'
/**
 * Inventory mutation hooks using factory patterns
 * Reduced from 232 lines to ~100 lines (57% reduction)
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
  ApproveInventoryAdjustment,
  CreateInventoryCategory,
  CreateInventoryCategoryTypeOption,
  CreateInventoryItem,
  CreateInventoryLocation,
  CreateInventoryMovementReasonOption,
  InventoryCategory,
  InventoryCategoryTypeOption,
  InventoryItem,
  InventoryLocation,
  InventoryMovementReasonOption,
  RejectInventoryAdjustment,
  UpdateInventoryCategory,
  UpdateInventoryCategoryTypeOption,
  UpdateInventoryItem,
  UpdateInventoryLocation,
  UpdateInventoryMovementReasonOption,
} from '@scrambl/shared'

import { queryKeys } from '../query-keys'
import { useWorkspaceSession } from '../useWorkspaceSession'
import { createSubResourceMutations } from './createSimpleMutation'

const logger = createLogger('hooks/mutations/useInventoryMutations')

// ============================================================================
// INVENTORY ITEMS - Using main factory with analytics invalidation
// ============================================================================

/** Apply reader-projected rows only to the session that submitted the edit. */
function useInventoryItemWrite<TVariables>(
  request: (variables: TVariables) => Promise<InventoryItem>,
) {
  const scope = useWorkspaceSession()
  const client = useQueryClient()
  return useMutation({
    onMutate: () => ({ scope }),
    mutationFn: (variables: TVariables) =>
      scope.run(new AbortController().signal, () => request(variables)),
    onSuccess: (item, _, context) => {
      const owner = context!.scope
      owner.assertUnchanged()
      client.setQueryData(
        scopedRecordKey(queryKeys.inventory.detail(item.id), owner),
        item,
      )
      client.setQueriesData(
        { queryKey: scopedRecordKey(queryKeys.inventory.lists(), owner) },
        (old: unknown) => {
          const replace = (row: { id?: string }) =>
            row.id === item.id ? item : row
          if (Array.isArray(old)) return old.map(replace)
          if (
            old &&
            typeof old === 'object' &&
            'data' in old &&
            Array.isArray(old.data)
          )
            return { ...old, data: old.data.map(replace) }
          return old
        },
      )
      invalidateAfterAdjustment(client, owner)
    },
    onError: (error, _, context) => {
      if (context?.scope.signal.aborted) return
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to save inventory item',
      )
    },
  })
}

export function useCreateInventoryItemMutation() {
  return useInventoryItemWrite(({ data }: { data: CreateInventoryItem }) =>
    inventoryApi.createInventoryItem(data),
  )
}
export function useUpdateInventoryItemMutation() {
  return useInventoryItemWrite(
    ({ id, data }: { id: string; data: UpdateInventoryItem }) =>
      inventoryApi.updateInventoryItem(id, data),
  )
}
export function useUpdateInventoryItemFieldMutation() {
  return useInventoryItemWrite(
    ({ id, field, value }: { id: string; field: string; value: unknown }) =>
      inventoryApi.updateInventoryItem(id, {
        [field]: value,
      } as UpdateInventoryItem),
  )
}

export function useDeleteInventoryItemMutation() {
  const scope = useWorkspaceSession()
  const client = useQueryClient()
  return useMutation({
    onMutate: () => ({ scope }),
    mutationFn: (id: string) =>
      scope.run(new AbortController().signal, () =>
        inventoryApi.deleteInventoryItem(id),
      ),
    onSuccess: (_, id, context) => {
      const owner = context!.scope
      owner.assertUnchanged()
      client.removeQueries({
        queryKey: scopedRecordKey(queryKeys.inventory.detail(id), owner),
      })
      client.setQueriesData(
        { queryKey: scopedRecordKey(queryKeys.inventory.lists(), owner) },
        (old: unknown) => {
          const keep = (row: { id?: string }) => row.id !== id
          if (Array.isArray(old)) return old.filter(keep)
          if (
            old &&
            typeof old === 'object' &&
            'data' in old &&
            Array.isArray(old.data)
          )
            return { ...old, data: old.data.filter(keep) }
          return old
        },
      )
      invalidateAfterAdjustment(client, owner)
      toast.success('Inventory item deleted successfully')
    },
    onError: (error, _, context) => {
      if (context?.scope.signal.aborted) return
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to delete inventory item',
      )
    },
  })
}

/**
 * Prefix matching every cached query of one inventory resource, whatever its
 * filters. Never use the no-arg `categories()` / `locations()` / `movements()`
 * keys for this: they end in an `undefined` filter slot, which TanStack's
 * partial matching compares against the tabs' filter objects and rejects, so
 * the invalidation silently misses every filtered list.
 */
function inventoryResourceKey(
  resource: 'categories' | 'locations' | 'movements',
  scope: ReturnType<typeof useWorkspaceSession>,
) {
  return scopedRecordKey([...queryKeys.inventory.all, resource], scope)
}

// ============================================================================
// CATEGORIES - Using sub-resource factory
// ============================================================================

function categoryMutations(scope: ReturnType<typeof useWorkspaceSession>) {
  const run = <T>(request: () => Promise<T>) =>
    scope.run(new AbortController().signal, request)
  return createSubResourceMutations<
    InventoryCategory,
    CreateInventoryCategory,
    UpdateInventoryCategory
  >({
    resourceName: 'Category',
    api: {
      create: data => run(() => inventoryApi.createCategory(data)),
      update: (id, data) => run(() => inventoryApi.updateCategory(id, data)),
      delete: id => run(() => inventoryApi.deleteCategory(id)),
    },
    primaryKey: () => inventoryResourceKey('categories', scope),
    additionalKeys: () => [scopedRecordKey(queryKeys.inventory.lists(), scope)],
  })
}
export function useCreateInventoryCategoryMutation() {
  return categoryMutations(useWorkspaceSession()).useCreateMutation()
}
export function useUpdateInventoryCategoryMutation() {
  return categoryMutations(useWorkspaceSession()).useUpdateMutation()
}
export function useDeleteInventoryCategoryMutation() {
  return categoryMutations(useWorkspaceSession()).useDeleteMutation()
}

// The update endpoint hands its body straight to Prisma, so only real
// category columns may be sent — table-only columns (itemCount, createdAt)
// and unknown keys must never reach it.
const CATEGORY_TEXT_FIELDS = new Set([
  'description',
  'color',
  'icon',
  'defaultIncomeAccountCode',
  'defaultExpenseAccountCode',
  'defaultAssetAccountCode',
  'taxCategoryCode',
])
const CATEGORY_REQUIRED_FIELDS: Record<string, string> = {
  name: 'Category name is required',
  code: 'Category code is required',
  type: 'Category type is required',
}

/** Map a table cell edit onto the category update DTO, or throw if it can't be saved. */
export function toInventoryCategoryUpdate(
  field: string,
  value: unknown,
): UpdateInventoryCategory {
  if (field in CATEGORY_REQUIRED_FIELDS) {
    const text = typeof value === 'string' ? value.trim() : ''
    if (!text) throw new Error(CATEGORY_REQUIRED_FIELDS[field])
    return { [field]: text }
  }
  if (CATEGORY_TEXT_FIELDS.has(field)) {
    const text = typeof value === 'string' ? value.trim() : ''
    return { [field]: text || null }
  }
  if (field === 'isActive') return { isActive: value === true }
  throw new Error('This category field cannot be edited here')
}

type CategoryRow = InventoryCategory & Record<string, unknown>

/**
 * Inline table edit for one category field. The row is patched in every
 * cached category list right away; a rejected save (e.g. the server's unique
 * code / case-insensitive unique name rules) restores the previous value and
 * surfaces the server message, then rethrows so the cell stays unsaved.
 */
export function useUpdateInventoryCategoryFieldMutation() {
  const scope = useWorkspaceSession()
  const client = useQueryClient()
  const categoriesKey = () => inventoryResourceKey('categories', scope)
  const patchRows = (
    id: string,
    patch: (row: CategoryRow) => CategoryRow,
    queryKey: readonly unknown[] = categoriesKey(),
  ) =>
    client.setQueriesData<CategoryRow[]>({ queryKey }, old =>
      Array.isArray(old)
        ? old.map(row => (row.id === id ? patch(row) : row))
        : old,
    )

  return useMutation({
    onMutate: async ({
      id,
      field,
      value,
    }: {
      id: string
      field: string
      value: unknown
    }) => {
      const data = toInventoryCategoryUpdate(field, value)
      const queryKey = categoriesKey()
      await client.cancelQueries({ queryKey })
      const previous = new Map<string, unknown>()
      for (const [key, rows] of client.getQueriesData<CategoryRow[]>({
        queryKey,
      })) {
        const row = Array.isArray(rows) && rows.find(r => r.id === id)
        if (row) previous.set(JSON.stringify(key), row[field])
      }
      patchRows(id, row => ({ ...row, ...data }), queryKey)
      return { scope, data, previous, queryKey }
    },
    mutationFn: ({
      id,
      field,
      value,
    }: {
      id: string
      field: string
      value: unknown
    }) =>
      scope.run(new AbortController().signal, () =>
        inventoryApi.updateCategory(
          id,
          toInventoryCategoryUpdate(field, value),
        ),
      ),
    onSuccess: (category, { id }, context) => {
      const owner = context.scope
      owner.assertUnchanged()
      patchRows(id, row => ({ ...row, ...category }), context.queryKey)
    },
    onError: (error, { id, field }, context) => {
      if (context?.scope.signal.aborted) return
      if (context) {
        // Only undo our own optimistic value — a later edit to the same cell
        // may already have replaced it.
        const optimistic = (context.data as Record<string, unknown>)[field]
        for (const [key, rows] of client.getQueriesData<CategoryRow[]>({
          queryKey: context.queryKey,
        })) {
          const hashed = JSON.stringify(key)
          if (!context.previous.has(hashed) || !Array.isArray(rows)) continue
          client.setQueryData<CategoryRow[]>(key, old =>
            old?.map(row =>
              row.id === id && row[field] === optimistic
                ? { ...row, [field]: context.previous.get(hashed) }
                : row,
            ),
          )
        }
      }
      logger.error('[useUpdateInventoryCategoryFieldMutation] Error:', error)
      toast.error(getErrorMessage(error, 'Failed to update category'))
    },
    onSettled: (_data, _error, _variables, context) => {
      if (!context || context.scope.signal.aborted) return
      // Filtered lists (by type) and item rows showing the category name.
      void client.invalidateQueries({ queryKey: context.queryKey })
      void client.invalidateQueries({
        queryKey: scopedRecordKey(queryKeys.inventory.lists(), context.scope),
      })
    },
  })
}

// ============================================================================
// CATEGORY TYPES — per-org lookup that backs the Type dropdown.
// ============================================================================

function categoryTypeMutations(scope: ReturnType<typeof useWorkspaceSession>) {
  const run = <T>(request: () => Promise<T>) =>
    scope.run(new AbortController().signal, request)
  return createSubResourceMutations<
    InventoryCategoryTypeOption,
    CreateInventoryCategoryTypeOption,
    UpdateInventoryCategoryTypeOption
  >({
    resourceName: 'Category type',
    api: {
      create: data => run(() => inventoryApi.createCategoryType(data)),
      update: (id, data) =>
        run(() => inventoryApi.updateCategoryType(id, data)),
      delete: id => run(() => inventoryApi.deleteCategoryType(id)),
    },
    primaryKey: () =>
      scopedRecordKey(queryKeys.inventory.categoryTypes(), scope),
    // The category list shows the resolved type label, so a rename should
    // bust the category cache too.
    additionalKeys: () => [inventoryResourceKey('categories', scope)],
  })
}
export function useCreateInventoryCategoryTypeMutation() {
  return categoryTypeMutations(useWorkspaceSession()).useCreateMutation()
}
export function useUpdateInventoryCategoryTypeMutation() {
  return categoryTypeMutations(useWorkspaceSession()).useUpdateMutation()
}
export function useDeleteInventoryCategoryTypeMutation() {
  return categoryTypeMutations(useWorkspaceSession()).useDeleteMutation()
}

// ============================================================================
// MOVEMENT REASONS — per-org lookup that backs the Reason dropdown.
// ============================================================================

function movementReasonMutations(
  scope: ReturnType<typeof useWorkspaceSession>,
) {
  const run = <T>(request: () => Promise<T>) =>
    scope.run(new AbortController().signal, request)
  return createSubResourceMutations<
    InventoryMovementReasonOption,
    CreateInventoryMovementReasonOption,
    UpdateInventoryMovementReasonOption
  >({
    resourceName: 'Movement reason',
    api: {
      create: data => run(() => inventoryApi.createMovementReason(data)),
      update: (id, data) =>
        run(() => inventoryApi.updateMovementReason(id, data)),
      delete: id => run(() => inventoryApi.deleteMovementReason(id)),
    },
    primaryKey: () =>
      scopedRecordKey(queryKeys.inventory.movementReasons(), scope),
    // Movements list shows the resolved label, so a rename should bust the
    // movements cache too.
    additionalKeys: () => [inventoryResourceKey('movements', scope)],
  })
}
export function useCreateInventoryMovementReasonMutation() {
  return movementReasonMutations(useWorkspaceSession()).useCreateMutation()
}
export function useUpdateInventoryMovementReasonMutation() {
  return movementReasonMutations(useWorkspaceSession()).useUpdateMutation()
}
export function useDeleteInventoryMovementReasonMutation() {
  return movementReasonMutations(useWorkspaceSession()).useDeleteMutation()
}

// ============================================================================
// LOCATIONS - Using sub-resource factory
// ============================================================================

function locationMutations(scope: ReturnType<typeof useWorkspaceSession>) {
  const run = <T>(request: () => Promise<T>) =>
    scope.run(new AbortController().signal, request)
  return createSubResourceMutations<
    InventoryLocation,
    CreateInventoryLocation,
    UpdateInventoryLocation
  >({
    resourceName: 'Location',
    api: {
      create: data => run(() => inventoryApi.createLocation(data)),
      update: (id, data) => run(() => inventoryApi.updateLocation(id, data)),
      delete: id => run(() => inventoryApi.deleteLocation(id)),
    },
    primaryKey: () => inventoryResourceKey('locations', scope),
    additionalKeys: () => [scopedRecordKey(queryKeys.inventory.lists(), scope)],
  })
}
export function useCreateInventoryLocationMutation() {
  return locationMutations(useWorkspaceSession()).useCreateMutation()
}
export function useUpdateInventoryLocationMutation() {
  return locationMutations(useWorkspaceSession()).useUpdateMutation()
}
export function useDeleteInventoryLocationMutation() {
  return locationMutations(useWorkspaceSession()).useDeleteMutation()
}

// ============================================================================
// MOVEMENTS - Manual due to complex invalidation with dynamic item ID
// ============================================================================

export function useRecordInventoryMovementMutation() {
  const queryClient = useQueryClient()
  const scope = useWorkspaceSession()

  return useMutation({
    onMutate: () => ({ scope }),
    mutationFn: async (
      data: Parameters<typeof inventoryApi.recordMovement>[0],
    ) => {
      return scope.run(new AbortController().signal, () =>
        inventoryApi.recordMovement(data),
      )
    },
    onSuccess: (_result, _variables, context) => {
      context!.scope.assertUnchanged()
      invalidateAfterAdjustment(queryClient, context!.scope)

      toast.success('Movement recorded successfully')
    },
    onError: (error, _variables, context) => {
      if (context?.scope.signal.aborted) return
      logger.error('[useRecordInventoryMovementMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to record movement',
      )
    },
  })
}

// ============================================================================
// ADJUSTMENTS — cycle counts, damage, corrections with optional approval
// ============================================================================

function invalidateAfterAdjustment(
  queryClient: ReturnType<typeof useQueryClient>,
  scope: ReturnType<typeof useWorkspaceSession>,
) {
  void queryClient.invalidateQueries({
    predicate: query =>
      isRecordQueryInScope(query.queryKey, scope) &&
      (query.queryKey[0] === 'inventory' ||
        (query.queryKey[0] === 'analytics' &&
          query.queryKey[1] === 'inventory-turns')),
  })
}

export function useCreateInventoryAdjustmentMutation() {
  const queryClient = useQueryClient()
  const scope = useWorkspaceSession()
  return useMutation({
    onMutate: () => ({ scope }),
    mutationFn: async (
      data: Parameters<typeof inventoryApi.createAdjustment>[0],
    ) =>
      scope.run(new AbortController().signal, () =>
        inventoryApi.createAdjustment(data),
      ),
    onSuccess: (adjustment, _variables, context) => {
      context!.scope.assertUnchanged()
      invalidateAfterAdjustment(queryClient, context!.scope)
      toast.success(
        adjustment.status === 'PENDING'
          ? 'Adjustment submitted for approval'
          : adjustment.status === 'APPLIED'
            ? 'Adjustment applied successfully'
            : 'Adjustment saved',
      )
    },
    onError: (error, _variables, context) => {
      if (context?.scope.signal.aborted) return
      logger.error('[useCreateInventoryAdjustmentMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to create adjustment',
      )
    },
  })
}

export function useApproveInventoryAdjustmentMutation() {
  const queryClient = useQueryClient()
  const scope = useWorkspaceSession()
  return useMutation({
    mutationFn: async (args: {
      id: string
      data?: ApproveInventoryAdjustment
    }) =>
      scope.run(new AbortController().signal, () =>
        inventoryApi.approveAdjustment(args.id, args.data ?? {}),
      ),
    onSuccess: () => {
      scope.assertUnchanged()
      invalidateAfterAdjustment(queryClient, scope)
      toast.success('Adjustment approved and applied')
    },
    onError: error => {
      logger.error('[useApproveInventoryAdjustmentMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to approve adjustment',
      )
    },
  })
}

export function useRejectInventoryAdjustmentMutation() {
  const queryClient = useQueryClient()
  const scope = useWorkspaceSession()
  return useMutation({
    mutationFn: async (args: {
      id: string
      data?: RejectInventoryAdjustment
    }) =>
      scope.run(new AbortController().signal, () =>
        inventoryApi.rejectAdjustment(args.id, args.data ?? {}),
      ),
    onSuccess: () => {
      scope.assertUnchanged()
      invalidateAfterAdjustment(queryClient, scope)
      toast.success('Adjustment rejected')
    },
    onError: error => {
      logger.error('[useRejectInventoryAdjustmentMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to reject adjustment',
      )
    },
  })
}
